import React, { useEffect, useMemo, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import TopArticle from "../../components/Blog/TopArticle";
import Articles from "../../components/Blog/Articles";
import Jumbotron from "../../components/Blog/Jumbotron";
import Filter from "../../components/Blog/Articles/Filter";
import Article from "../../components/Blog/Articles/Article";
import Grid from "../../components/Blog/Grid";
import styles from "./index.module.scss";
import * as querystring from "qs";
import Subscription from "../../components/Blog/Subscription";
import cn from "classnames";
import ContributeBanner from "components/Blog/ContributeBanner";
import Featured from "components/Blog/Featured";
import { BLOG_METADATA } from "../../shared/metadata";

export const PER_PAGE = 8;

const growthData = {
  color: "#7BBDF1",
  image: "/blog/white-paper.png",
  title: "5 App Growth Cases for Subscription Apps",
  description:
    "This white paper examines five distinct growth cases that subscription apps can leverage to remain competitive in the mobile market.",
  created: "2024-03-15T08:33:49.050Z",
  avatar:
    "https://static.apphud.com/blog/users/6253fe79253eb30026e2435f/thumb_417292e5-e5d7-4c3e-8226-1b0afd3ad856.jpg",
  author: "Nataly",
  readingTime: 1,
  identifier: "growth-cases-for-subscription-apps",
};

const subsData = {
  color: "#8685FF",
  image: "/blog/sub.png",
  title: "Subscription App Launch Checklist",
  description:
    "The Apphud Subscription App Launch checklist will help you cover all the technical, product, and marketing bases and ensure your app launch goes as smoothly as possible.",
  created: "2024-02-21T08:33:49.050Z",
  avatar:
    "https://static.apphud.com/blog/users/6253fe79253eb30026e2435f/thumb_417292e5-e5d7-4c3e-8226-1b0afd3ad856.jpg",
  author: "Nataly",
  readingTime: 1,
};

export default function Blog({
  articles = {},
  categories = {},
  featuredArticles = {},
}) {
  const router = useRouter();
  const [items, setItems] = useState(articles.results);
  const [meta, setMeta] = useState(articles.meta);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const initialFilters = {
    page: 1,
    per_page: PER_PAGE + 1,
  };

  if (router.pathname.includes("categories")) {
    initialFilters.category = router.query.slug;
    delete initialFilters.tag;
  }

  if (router.pathname.includes("tag")) {
    initialFilters.tag = router.query.slug;
    delete initialFilters.category;
  }

  const [filter, setFilter] = useState({
    ...initialFilters,
  });

  const { featured = {}, tags = [] } = meta;

  const getPosts = (query, callback) => {
    setLoading(true);
    const args = querystring.stringify(query);
    fetch(`https://api.apphud.com/blog/posts?${args}`)
      .then((r) => r.json())
      .then(callback)
      .finally(() => setLoading(false));
  };

  const onPaginateHandler = (value) => {
    getPosts({ ...filter, page: value, per_page: PER_PAGE + 2 }, (data) => {
      setPage(value);
      setItems([...items].concat(data.results));
      setMeta(data.meta);
    });
  };

  useEffect(() => {
    getPosts(filter, (data) => {
      setItems(data.results);
      setMeta(data.meta);
    });
  }, [filter]);

  const onSetTagHandler = (tag) => {
    const query = {
      ...filter,
      page: 1,
      category: tag.identifier,
      tag: null,
    };
    setFilter(query);
  };

  const onInputFieldHandler = (search) => {
    const query = {
      ...filter,
      page: 1,
      q: search,
    };
    setFilter(query);
  };

  useEffect(() => {
    setItems(articles.results);
    setMeta(articles.meta);
    setFilter({
      ...filter,
      page: 1,
    });
    setPage(1);
  }, [articles]);

  const getContent = useMemo(() => {
    const add = [];
    items.map((article, i) => {
      if (article.published_at === "2024-03-13T10:45:56.712Z") {
        add.push(
          <Link
            href={"/blog/growth-cases-for-subscription-apps"}
            key={growthData.title}
          >
            <a>
              <Article {...growthData} />
            </a>
          </Link>
        );
      } else if (article.published_at === "2024-02-14T09:58:50.121Z") {
        add.push(
          <Link
            href={"/blog/subscription-app-launch-checklist"}
            key={subsData.title}
          >
            <a>
              <Article {...subsData} />
            </a>
          </Link>
        );
      }

      add.push(
        <Link
          href={`/blog/${article.identifier}`}
          key={`${article.title}-${article.identifier}`}
        >
          <a>
            <Article
              color={article.color}
              readingTime={article.reading_time}
              image={article?.icon.thumb}
              title={article.title}
              description={article.pretext}
              avatar={article.user?.avatar?.thumb}
              author={`${article.user?.first_name} ${article.user?.last_name} `}
              created={article.published_at || article.created_at}
            />
          </a>
        </Link>
      );
    });
    add.splice(3, 0, <Subscription key={"subscription"} />);
    add.splice(
      6,
      0,
      <div className="fullWidthItem" key="featured-articles">
        <Featured articles={[growthData, ...featuredArticles]} />
      </div>
    );
    add.splice(7, 0, <ContributeBanner key="contribute-banner" />);
    return add;
  }, [items]);

  const metaData = useMemo(() => {
    const category = router.query.slug;

    return BLOG_METADATA[category]
      ? BLOG_METADATA[category]
      : BLOG_METADATA.blog;
  }, [router.query.slug]);

  return (
    <div className={`wrapper ${styles.container}`}>
      <Head>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
        <link rel="canonical" href="https://apphud.com/blog" />
      </Head>
      <div className={cn("container", styles.header)}>
        <h1 className={styles.title}>
          Everything you need to know about App Revenue Growth
        </h1>
      </div>
      {featured?.title && (
        <Jumbotron>
          <Link href={`/blog/${featured.identifier}`}>
            <a>
              <TopArticle
                title={featured?.title}
                color={featured.color}
                readingTime={featured.reading_time}
                image={featured?.featured_image?.thumb}
                detailLink={null}
              >
                {featured?.pretext}
              </TopArticle>
            </a>
          </Link>
        </Jumbotron>
      )}
      <Articles isTransparent={true}>
        <div className="container">
          <Filter
            categories={categories.results}
            tags={tags}
            onChange={onSetTagHandler}
            onInputFieldHandler={onInputFieldHandler}
          />
          <Grid
            loading={loading}
            page={page}
            pageCount={meta?.pages_count}
            onPaginate={onPaginateHandler}
          >
            {getContent}
            {meta.pages_count === 0 && <h5>No data...</h5>}
          </Grid>
        </div>
      </Articles>
    </div>
  );
}

export async function getServerSideProps(context) {
  const { query, params } = context;
  const args = {
    page: 1,
    per_page: PER_PAGE + 1,
  };

  if (context.type === "category") {
    args.category = query.slug || params?.slug;
  }
  if (context.type === "tag") {
    args.tag = query.slug || params?.slug;
  }

  const stringifiedArgs = querystring.stringify(args);
  const articles = await fetch(
    `https://api.apphud.com/blog/posts/?${stringifiedArgs}`
  );

  const categories = await fetch("https://api.apphud.com/blog/categories");

  const featuredUrls = [
    "https://api.apphud.com/blog/posts/best-performing-paywallls",
    "https://api.apphud.com/blog/posts/use-case-arppu-nday",
    "https://api.apphud.com/blog/posts/scale-app-revenue-with-apphud",
    "https://api.apphud.com/blog/posts/increase-app-revenue",
    "https://api.apphud.com/blog/posts/ab-testing-with-apphud",
  ];

  const featuredArticlesPromises = featuredUrls.map((url) =>
    fetch(url).then((res) => res.json())
  );

  // eslint-disable-next-line no-undef
  const featuredArticles = await Promise.all(featuredArticlesPromises);

  return {
    props: {
      articles: await articles.json(),
      categories: await categories.json(),
      featuredArticles: featuredArticles.map(
        (featuredArticle) => featuredArticle.results
      ),
    },
  };
}
